import { forkJoin } from 'rxjs';
import { map, mergeMap, switchMap, filter, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { OrdersService } from '@core/services/orders/orders.service';
import {
  ORDER_STORE_ACTIVE_ACTIONS,
  ORDER_STORE_API_ACTIONS,
  ORDER_STORE_LOCAL_ACTIONS,
} from '@store/order/order.action';
import { SETTINGS_STORE_LOCAL_ACTIONS } from '@store/settings';

@Injectable({
  providedIn: 'root',
})
export class OrderEffects {
  private readonly _actions = inject(Actions);
  private readonly _ordersService = inject(OrdersService);

  // Note: For now, Cannot react to cart/basket state - checkCartIsValid$ updates cart (user, cart_id)
  // which would retrigger validation in an infinite loop
  checkCartIsValid = createEffect(() => {
    return this._actions.pipe(
      ofType(ORDER_STORE_API_ACTIONS.checkCartIsValid),
      switchMap(() =>
        this._ordersService.checkCartIsValid$().pipe(
          map(result => ORDER_STORE_API_ACTIONS.checkCartIsValidComplete({ result })),
          catchError(() => of(ORDER_STORE_API_ACTIONS.checkCartIsValidComplete({ result: null }))),
        ),
      ),
    );
  });

  fetchOrders = createEffect(() => {
    return this._actions.pipe(
      ofType(ORDER_STORE_API_ACTIONS.fetchOrders),
      switchMap(({ userId, marketplaceId }) =>
        forkJoin({
          active: this._ordersService.getActiveOrders$(userId, marketplaceId),
          historical: this._ordersService.getHistoricalOrders$(userId, marketplaceId),
        }),
      ),
      mergeMap(({ active, historical }) => {
        return [
          ORDER_STORE_LOCAL_ACTIONS.updateIsLoading({ isLoading: true }),
          ORDER_STORE_API_ACTIONS.fetchOrdersSuccess({ orders: [...active, ...historical] }),
        ];
      }),
    );
  });

  fetchOrdersSuccess = createEffect(() => {
    return this._actions.pipe(
      ofType(ORDER_STORE_API_ACTIONS.fetchOrdersSuccess),
      mergeMap(({ orders }) => {
        return [
          ORDER_STORE_ACTIVE_ACTIONS.setAll({ entities: orders }),
          ORDER_STORE_LOCAL_ACTIONS.updateIsLoaded({ isLoaded: true }),
          ORDER_STORE_API_ACTIONS.fetchOrdersComplete(),
        ];
      }),
    );
  });

  fetchOrdersFailure = createEffect(() => {
    return this._actions.pipe(
      ofType(ORDER_STORE_API_ACTIONS.fetchOrdersFailure),
      mergeMap(({ error }) => {
        return [
          ORDER_STORE_LOCAL_ACTIONS.updateErrors({ errors: { error } }),
          ORDER_STORE_LOCAL_ACTIONS.updateIsLoaded({ isLoaded: false }),
          ORDER_STORE_API_ACTIONS.fetchOrdersComplete(),
        ];
      }),
    );
  });

  fetchOrdersComplete = createEffect(() => {
    return this._actions.pipe(
      ofType(ORDER_STORE_API_ACTIONS.fetchOrdersComplete),
      mergeMap(() => {
        return [ORDER_STORE_LOCAL_ACTIONS.updateIsLoading({ isLoading: false })];
      }),
    );
  });

  /**
   * Case: A first order is created with a wanted_at for 19h today
   * - Then another order is created with a wanted_at for 20h today
   * The cart parameters modal will still show 19h (because it take the time of the active order)
   * #DOOD-1584
   */
  resetActiveOrderOnWantedAtChange = createEffect(() => {
    return this._actions.pipe(
      ofType(SETTINGS_STORE_LOCAL_ACTIONS.updateParameters),
      filter(data => !!data.parameters?.wanted_at),
      map(() => ORDER_STORE_ACTIVE_ACTIONS.resetActive()),
    );
  });
}
